/**
 * Provides an inventory table used for editing a single inventory item.
 * A styled container is provider to override styles for the group summary rows.
 */
import React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { AssetType } from '@kentik/ui-shared/labels/lib';
import { ModelBase } from 'core/model/model.types';
import { Flex } from 'core/components';
import AssetTagInventoryCollection from 'app/stores/assetTags/AssetTagInventoryCollection';
import useInventoryCollection from 'app/views/settings/assetTags/hooks/useInventoryCollection';
import InventoryTable from './InventoryTable';

/**
 * Locks down group summary rows to render with:
 * - plain background color (theme compatible)
 * - no expand/collapse button
 * - normal font weight
 *
 * This results in a row that more closely resembles a standard row.
 */
const Container = styled(Flex)`
  font-weight: normal;

  .group-summary {
    font-weight: normal;
    background: none !important;

    &:hover {
      background: ${({ theme }) => theme.colors.waterfall.headerBackground} !important;
    }

    & > div:first-child > button {
      display: none;
    }
  }

  & div:last-child.group-summary {
    border-bottom: ${({ theme }) => theme.borders.thin};
  }
`;

interface Props {
  assetType: AssetType;
  model: ModelBase;
  onLoad?: (args: { collection: AssetTagInventoryCollection }) => void;
}

function SingleUpdate(props: Props) {
  const { assetType, model, onLoad } = props;
  const { collection, onSearch } = useInventoryCollection({
    assetType,
    assetModel: model,
    onLoad
  });

  return (
    <Container mx={1} minHeight={500}>
      <InventoryTable assetType={assetType} collection={collection} onSearch={onSearch} singleMode />
    </Container>
  );
}

export default observer(SingleUpdate);
